import { api } from '@helpers/axios'
import config from '@config/index'
import notification from '@components/notification'

/**
 * Invoice attachment service using centralized attachment API
 * This service communicates with the new dashboard attachment utility
 */

/**
 * Upload invoice attachments to centralized attachment service
 * @param {Object} params
 * @param {File} params.file - File to upload
 * @param {Object} params.axiosConfig - Axios config for progress tracking
 * @param {String} params.action - Action name for prefix mapping (invoice, getpaid, direct, etc.)
 * @param {String} params.prefix - DEPRECATED: Use action instead. Kept for backward compatibility
 * @returns {Promise} Upload response
 */
export function uploadInvoiceAttachments({ file, axiosConfig, action, prefix }) {
  const body = new FormData()
  body.append('attachments', file)

  // Prefer action over prefix (prefix is deprecated but kept for backward compatibility)
  const queryParam = action
    ? `?action=${encodeURIComponent(action)}`
    : prefix
    ? `?prefix=${encodeURIComponent(prefix)}`
    : ''
  return api()
    .post(`${config.server}/api/invoices/attachments${queryParam}`, body, axiosConfig)
    .then((res) => {
      return res.data
    })
    .catch((e) => {
      console.error('Invoice attachment upload failed:', e)
      const errorMessage = (e.response && e.response.data && e.response.data.message) || e.message || 'Upload failed'
      notification('error', errorMessage)
      throw e
    })
}

/**
 * NEW: Prepare temporary attachments for integrated invoice creation
 * This function formats temporary attachments for the new integrated workflow
 * @param {Array} uploadedAttachments - Array of uploaded attachment objects
 * @returns {Array} Formatted attachments for invoice creation
 */
export function prepareAttachmentsForInvoiceCreation(uploadedAttachments) {
  if (!uploadedAttachments || uploadedAttachments.length === 0) {
    return []
  }

  // Format attachments for the new integrated workflow
  return uploadedAttachments
    .map((attachment) => ({
      key: attachment.key || attachment.s3Key, // Keep the temporary key (tmpinv_ prefix)
      originalname: attachment.originalname || attachment.name,
      size: attachment.size,
      mimetype: attachment.mimetype,
      // Add any additional metadata needed
      uploaded_at: attachment.uploaded_at || new Date().toISOString()
    }))
    .filter((a) => !!a.key)
}

/**
 * Download invoice attachment
 * @param {String} s3Key - S3 key of the attachment
 * @returns {String} Download URL
 */
export function getInvoiceAttachmentDownloadUrl(s3Key) {
  // Return the download URL for the attachment
  return `${config.server}/api/attachments/invoices/${encodeURIComponent(s3Key)}`
}

/**
 * Validate file before upload
 * @param {File} file - File to validate
 * @returns {Object} Validation result
 */
export function validateInvoiceAttachmentFile(file) {
  const maxFileSize = 5 * 1024 * 1024 // 5MB
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml'
  ]

  const errors = []

  // Check file size
  if (file.size > maxFileSize) {
    errors.push({
      code: 'FILE_TOO_LARGE',
      message: `File "${file.name}" is too large. Maximum size allowed is 10MB.`
    })
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    // Also check by file extension as fallback
    const extension = file.name.toLowerCase().split('.').pop()
    const allowedExtensions = ['pdf', 'doc', 'docx', 'txt', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']

    if (!allowedExtensions.includes(extension)) {
      errors.push({
        code: 'INVALID_FILE_TYPE',
        message: `File type "${
          file.type || extension
        }" is not allowed. Supported formats: PDF, DOC, DOCX, TXT, JPG, PNG, GIF, WEBP, SVG.`
      })
    }
  }

  // Check filename for special characters
  const invalidChars = /[<>:"/\\|?*]/g
  if (invalidChars.test(file.name)) {
    errors.push({
      code: 'INVALID_FILENAME',
      message: `File name "${file.name}" contains invalid characters. Please rename the file.`
    })
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Process multiple files for validation
 * @param {Array} files - Array of files to validate
 * @param {Array} existingFiles - Already uploaded/uploading files
 * @param {Number} maxFiles - Maximum number of files allowed
 * @returns {Object} Validation result with valid files
 */
export function validateInvoiceAttachmentFiles(files, existingFiles = [], maxFiles = 5) {
  const errors = []
  const validFiles = []

  // Check total file count
  if (files.length + existingFiles.length > maxFiles) {
    errors.push({
      code: 'TOO_MANY_FILES',
      message: `Maximum ${maxFiles} files allowed. You have ${existingFiles.length} files and trying to add ${files.length} more.`
    })
    return { isValid: false, errors, validFiles: [] }
  }

  // Check for duplicates
  const existingNames = existingFiles.map((f) => f.name || f.originalName)
  const duplicates = []

  files.forEach((file) => {
    // Check if file already exists
    if (existingNames.includes(file.name) || validFiles.some((f) => f.name === file.name)) {
      duplicates.push(file.name)
      return
    }

    // Validate individual file
    const validation = validateInvoiceAttachmentFile(file)
    if (validation.isValid) {
      validFiles.push(file)
    } else {
      errors.push(...validation.errors)
    }
  })

  if (duplicates.length > 0) {
    errors.push({
      code: 'DUPLICATE_FILES',
      message: `Duplicate files detected: ${duplicates.join(', ')}`
    })
  }

  return {
    isValid: errors.length === 0,
    errors,
    validFiles
  }
}

// ===== PAYMENT LINK ATTACHMENT FUNCTIONS =====

/**
 * Upload payment link attachments to centralized attachment service
 * @param {Object} params
 * @param {File} params.file - File to upload
 * @param {Object} params.axiosConfig - Axios config for progress tracking
 * @param {String} params.action - Action name for prefix mapping (invoice, getpaid, direct, etc.)
 * @param {String} params.prefix - DEPRECATED: Use action instead. Kept for backward compatibility
 * @returns {Promise} Upload response
 */
export function uploadPaymentLinkAttachments({ file, axiosConfig, action, prefix }) {
  const body = new FormData()
  body.append('attachments', file)

  // Prefer action over prefix (prefix is deprecated but kept for backward compatibility)
  const queryParam = action
    ? `?action=${encodeURIComponent(action)}`
    : prefix
    ? `?prefix=${encodeURIComponent(prefix)}`
    : ''
  return api()
    .post(`${config.server}/api/invoices/attachments${queryParam}`, body, axiosConfig)
    .then((res) => {
      return res.data
    })
    .catch((e) => {
      console.error('Payment link attachment upload failed:', e)
      const errorMessage = (e.response && e.response.data && e.response.data.message) || e.message || 'Upload failed'
      notification('error', errorMessage)
      throw e
    })
}

/**
 * Download payment link attachment
 * @param {String} s3Key - S3 key of the attachment
 * @returns {String} Download URL
 */
export function getPaymentLinkAttachmentDownloadUrl(s3Key) {
  // Use the existing invoice attachment download URL for now
  return `${config.server}/api/attachments/invoices/${encodeURIComponent(s3Key)}`
}
